import { getMainFillJustification } from '@miles/domain/Dashboard/Application/FillJustification/fillJustifications'
import {
  AdultJustificativeType,
  Federation,
  FillJustificationStatus
} from '@prisma/client'
import { sub } from 'date-fns'
import { Array, Option, pipe, Record } from 'effect'
import { match, P } from 'ts-pattern'
import { z } from 'zod'
import { isMinor } from 'components/common/date.utils'
import { Attendee } from 'domain/models/AttendeeAggregate/Attendee'
import {
  AttendeeFormData,
  AttendeeFormDataDocument,
  AttendeeFormDataDocumentVerification
} from 'domain/models/AttendeeFormData/AttendeeFormData'
import {
  AccessRule,
  LicenseAndSupportingDocumentOnly,
  LicenseOnly,
  SupportingDocumentOnly
} from 'domain/models/AttendeeFormSchema/AccessRule'
import {
  AttendeeAgeStatus,
  MakeAttendeeFormSchemaContext
} from 'domain/models/AttendeeFormSchema/MakeAttendeeFormSchemaContext'
import { SpecialFederationName } from 'domain/models/AttendeeFormSchema/SpecialFederationName'
import { isMatchingT } from 'helpers/isMatchingT'
import { recordFromEntries } from 'helpers/records'
import { makeFFACompleteLicenseAndSupportingDocumentNowSchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/makeFFACompleteLicenseAndSupportingDocumentOnlyNowSchema'
import { FormSchema } from 'services/validation/form/form.zod'
import { MINOR_HEALTH_QUESTIONS_KEYS } from './AttendeeForm/AttendeeFormSchema/HealthQuestionnaireSchema'

const allAnswersToFalse = recordFromEntries(
  MINOR_HEALTH_QUESTIONS_KEYS.map((k) => [k, false])
)

const isMatchingContext = isMatchingT<MakeAttendeeFormSchemaContext>
const isMatchingAccessRule = isMatchingT<AccessRule>

const isSpecialFFTRIPattern = isMatchingContext({
  accessRule: { specialFederationWithId: { name: 'FFTRI' } }
})
const isLicenseAndSupportingDocumentOnlyInContext = isMatchingContext({
  accessRule: {
    type: 'LicenseAndSupportingDocumentOnly'
  }
})
const isMinorFreeAccess = isMatchingContext({
  accessRule: {
    type: 'SupportingDocumentOnly',
    acceptedMinorSupportingDocument: P.union('NONE', P.nullish)
  }
})
const isAdultFreeAccess = isMatchingContext({
  accessRule: {
    type: 'SupportingDocumentOnly',
    acceptedAdultSupportingDocument: P.union('NONE', P.nullish)
  }
})

const isSpecialFFAPatternWithPPS = (
  accessRule: AccessRule
): accessRule is LicenseAndSupportingDocumentOnly =>
  isMatchingAccessRule({
    specialFederationWithId: { name: 'FFA' },
    type: 'LicenseAndSupportingDocumentOnly'
  })(accessRule)

export class AttendeeFormService {
  constructor(readonly attendeeFormContext: MakeAttendeeFormSchemaContext) {}

  shouldDisplayDocumentForm() {
    if (
      this.attendeeFormContext.accessRule.type !== 'FreeAccess' &&
      this.attendeeFormContext.attendeeAgeStatus !== 'UNKNOWN'
    ) {
      if (this.attendeeFormContext.attendeeAgeStatus === 'MINOR') {
        return !isMinorFreeAccess(this.attendeeFormContext)
      }
      return !isAdultFreeAccess(this.attendeeFormContext)
    }
    return false
  }

  shouldShowCompleteLater(isLateRegistration: boolean): boolean {
    return !isLateRegistration && !this.isSpecialFFTRI()
  }

  isSpecialFFTRI(): boolean {
    return isSpecialFFTRIPattern(this.attendeeFormContext)
  }

  shouldShowLicenseQuestion(): boolean {
    return (
      this.isSpecialFFTRI() ||
      isLicenseAndSupportingDocumentOnlyInContext(this.attendeeFormContext)
    )
  }

  /**
   * This method can be killed after the FFA transition and period and there are no more availability of the form for the attendees of former races
   * @returns
   */
  shouldShowAlternativeFFAPPS(): boolean {
    if (isSpecialFFAPatternWithPPS(this.attendeeFormContext.accessRule)) {
      return makeFFACompleteLicenseAndSupportingDocumentNowSchema(
        this.attendeeFormContext.accessRule,
        this.attendeeFormContext
      ).shape.document.options.some(
        ({ shape }) => shape.completeType.value === 'FFA_PPS'
      )
    }
    return false
  }

  /**
   * This method can be killed after the FFA transition and period and there are no more availability of the form for the attendees of former races
   * @returns
   */
  shouldShowAlternativeFFAPPSTooEarly(): boolean {
    if (isSpecialFFAPatternWithPPS(this.attendeeFormContext.accessRule)) {
      return makeFFACompleteLicenseAndSupportingDocumentNowSchema(
        this.attendeeFormContext.accessRule,
        this.attendeeFormContext
      ).shape.document.options.some(
        ({ shape }) => shape.completeType.value === 'FFA_PPS_TOO_EARLY'
      )
    }
    return false
  }

  /**
   * This method can be killed after the FFA transition and period and there are no more availability of the form for the attendees of former races
   * @returns
   */
  shouldShowAlternativeFFAPPSMedicalCertificate(): boolean {
    if (isSpecialFFAPatternWithPPS(this.attendeeFormContext.accessRule)) {
      return makeFFACompleteLicenseAndSupportingDocumentNowSchema(
        this.attendeeFormContext.accessRule,
        this.attendeeFormContext
      ).shape.document.options.some(
        ({ shape }) => shape.completeType.value === 'MEDICAL_CERTIFICATE'
      )
    }
    return false
  }

  isFederationWithCheck(
    federationName: Federation
  ): federationName is SpecialFederationName {
    return match(this.attendeeFormContext)
      .with(
        {
          accessRule: {
            specialFederationWithId: { federationRaceId: P.string }
          }
        },
        ({
          accessRule: {
            specialFederationWithId: { name }
          }
        }) => name === federationName
      )
      .otherwise(() => false)
  }

  getNewLicenseStateFrom(
    state: AttendeeFormDataDocument | undefined
  ): AttendeeFormDataDocument {
    return match(this.attendeeFormContext.accessRule)
      .returnType<AttendeeFormDataDocument>()
      .with(
        {
          specialFederationWithId: { name: P.string },
          acceptedFederations: P.array()
        },
        ({
          specialFederationWithId,
          acceptedFederations
        }): AttendeeFormDataDocument => {
          if (
            specialFederationWithId.name === state?.federationName ||
            acceptedFederations.length === 1
          ) {
            return {
              ...state,
              documentNumber:
                state?.completeType !== 'LICENSE_AUTO_VERIFIED'
                  ? ''
                  : state.documentNumber,
              documentVerification:
                state?.completeType !== 'LICENSE_AUTO_VERIFIED'
                  ? undefined
                  : state.documentVerification,
              completeType: 'LICENSE_AUTO_VERIFIED'
            }
          } else {
            return { ...state, completeType: 'LICENSE_MANUALLY_VERIFIED' }
          }
        }
      )
      .otherwise(() => ({
        ...state,
        completeType: 'LICENSE_MANUALLY_VERIFIED'
      }))
  }

  getNewSupportingDocumentStateFrom(
    state: AttendeeFormDataDocument | undefined
  ): AttendeeFormDataDocument | undefined {
    return match(this.attendeeFormContext)
      .returnType<AttendeeFormDataDocument | undefined>()
      .with(
        {
          accessRule: { specialFederationWithId: { name: 'FFTRI' } }
        },
        () => ({ ...state, completeType: 'FFTRI_BUY_PASS_COMPETITION' })
      )
      .with(
        {
          accessRule: {
            specialFederationWithId: { name: 'FFA' },
            type: 'LicenseAndSupportingDocumentOnly'
          },
          attendeeAgeStatus: P.not(AttendeeAgeStatus.UNKNOWN)
        },
        ({ attendeeAgeStatus, accessRule }) => {
          if (attendeeAgeStatus === 'MINOR') {
            return {
              ...state,
              ...match(accessRule.acceptedMinorSupportingDocument)
                .with('HEALTH_QUESTIONNAIRE', (completeType) => ({
                  completeType,
                  answers: state?.answers ?? {}
                }))
                .with('CHECKBOX_AUTHORIZATION', (completeType) => ({
                  completeType,
                  checked: state?.checked
                }))
                .with('PARENTAL_AUTHORIZATION', (completeType) => ({
                  uploadedDocumentKey: state?.uploadedDocumentKey,
                  completeType
                }))
                .with(
                  'NONE',
                  () =>
                    ({
                      completeType: 'NO_SUPPORTING_DOCUMENT_REQUIRED'
                    } as const)
                )
                .exhaustive()
            }
          }
          // Below is for adult case
          if (this.shouldShowAlternativeFFAPPSTooEarly()) {
            // We keep the completeType as Medical certificate if an uploadedDocumentKey is present in state
            return state?.uploadedDocumentKey
              ? { ...state, completeType: 'MEDICAL_CERTIFICATE' }
              : { completeType: 'FFA_PPS_TOO_EARLY' }
          }
          if (this.shouldShowAlternativeFFAPPS()) {
            // We keep the completeType as Medical certificate if an uploadedDocumentKey is present in state
            return state?.uploadedDocumentKey
              ? { ...state, completeType: 'MEDICAL_CERTIFICATE' }
              : {
                  ...state,
                  documentNumber:
                    state?.completeType !== 'FFA_PPS'
                      ? ''
                      : state.documentNumber,
                  documentVerification:
                    state?.completeType !== 'FFA_PPS'
                      ? undefined
                      : state.documentVerification,
                  uploadedDocumentKey:
                    state?.documentVerification?.uploadedDocumentKey,
                  completeType: 'FFA_PPS'
                }
          }
          if (this.shouldShowAlternativeFFAPPSMedicalCertificate()) {
            return { ...state, completeType: 'MEDICAL_CERTIFICATE' }
          }

          return undefined
        }
      )
      .with(
        {
          accessRule: {
            type: P.union(
              'LicenseAndSupportingDocumentOnly',
              'SupportingDocumentOnly'
            )
          },
          attendeeAgeStatus: P.not(AttendeeAgeStatus.UNKNOWN)
        },
        ({ accessRule, attendeeAgeStatus }) => {
          if (attendeeAgeStatus === 'MINOR') {
            return match(accessRule.acceptedMinorSupportingDocument)
              .with('HEALTH_QUESTIONNAIRE', (completeType) => ({
                completeType,
                answers: state?.answers ?? {}
              }))
              .with('CHECKBOX_AUTHORIZATION', (completeType) => ({
                completeType,
                checked: state?.checked
              }))
              .with('PARENTAL_AUTHORIZATION', (completeType) => ({
                completeType
              }))
              .with(
                'NONE',
                () =>
                  ({
                    completeType: 'NO_SUPPORTING_DOCUMENT_REQUIRED'
                  } as const)
              )
              .exhaustive()
          } else {
            return accessRule.acceptedAdultSupportingDocument !== 'NONE'
              ? {
                  ...state,
                  completeType: accessRule.acceptedAdultSupportingDocument
                }
              : ({
                  completeType: 'NO_SUPPORTING_DOCUMENT_REQUIRED'
                } as const)
          }
        }
      )
      .otherwise(() => undefined)
  }

  getNewDocumentStateFromState(
    state: AttendeeFormDataDocument | undefined
  ): AttendeeFormDataDocument | undefined {
    if (!state) {
      if (this.shouldShowLicenseQuestion()) {
        return undefined
      } else if (
        this.attendeeFormContext.accessRule.type === 'SupportingDocumentOnly'
      ) {
        return this.getNewSupportingDocumentStateFrom(state)
      } else {
        return this.getNewLicenseStateFrom(state)
      }
    }
    if (
      state.completeType === 'LICENSE_AUTO_VERIFIED' ||
      state.completeType === 'LICENSE_MANUALLY_VERIFIED'
    ) {
      return this.getNewLicenseStateFrom(state)
    } else {
      return this.getNewSupportingDocumentStateFrom(state)
    }
  }

  resetForm(
    raw: AttendeeForReset,
    formOptions: Record<
      string,
      { key: string; value: FormSchema['defaultValue']; completeLater: boolean }
    > = {}
  ): AttendeeFormData {
    const data: AttendeeForReset = {
      ...raw,
      // birthdate is a string in the raw data (type issue)
      birthDate: raw.birthDate ? z.coerce.date().parse(raw.birthDate) : null
    }

    const { document, completeLater } = match<
      MakeAttendeeFormSchemaContext,
      Pick<AttendeeFormData, 'completeLater' | 'document'>
    >(this.attendeeFormContext)
      .with({ accessRule: { type: 'FreeAccess' } }, () => ({}))
      .with({ accessRule: { type: 'LicenseOnly' } }, ({ accessRule }) => {
        return fromLicenseAccessRule(data)(accessRule)
      })
      .with(
        { accessRule: { type: 'SupportingDocumentOnly' } },
        ({ accessRule }) => {
          return fromSupportingDocumentAccessRule(data)(
            accessRule,
            this.attendeeFormContext
          )
        }
      )
      .with(
        { accessRule: { type: 'LicenseAndSupportingDocumentOnly' } },
        ({ accessRule }) => {
          if (
            accessRule.specialFederationWithId?.name === 'FFA' &&
            data.licenseNumber?.startsWith('P') &&
            data.licenseNumber.length > 7
          ) {
            return {
              completeLater: false,
              document: {
                completeType: 'FFA_PPS',
                documentNumber: data.licenseNumber,
                documentVerification: data.medicalCertificate
                  ? {
                      uploadedDocumentKey: data.medicalCertificate.key,
                      verificationStatus:
                        data.medicalCertificate.status === 'VALID'
                          ? 'VERIFIED'
                          : data.medicalCertificate.status || undefined,
                      verifiedAt: data.medicalCertificate.submittedAt
                    }
                  : {
                      verificationStatus: 'VERIFIED',
                      verifiedAt: sub(new Date(), { days: 1 })
                    }
              }
            }
          } else if (data.licenseNumber) {
            return fromLicenseAccessRule(data)(accessRule)
          } else {
            if (accessRule.specialFederationWithId?.name === 'FFTRI') {
              return fromFFTRIPassCompetition(data)
            }

            return fromSupportingDocumentAccessRule(data)(
              accessRule,
              this.attendeeFormContext
            )
          }
        }
      )
      .exhaustive()

    const options: Record<
      string,
      { value: FormSchema['defaultValue']; completeLater: boolean }
    > = Record.map(formOptions, (option) => ({
      value: option.value,
      completeLater: match(option.value)
        .with({ additionalDocumentId: '' }, () =>
          // If no additional document is uploaded, we check if the fill justification is submitted
          pipe(
            raw.fillJustifications,
            Array.findFirst(({ formKey }) => formKey === option.key),
            Option.map(
              ({ status }) => status === FillJustificationStatus.UNSUBMITTED
            ),
            Option.getOrElse(() => false)
          )
        )
        .with({ additionalDocumentId: P.string }, () => false)
        .otherwise(() => true)
    }))

    const newData: AttendeeFormData = {
      ...this.getAttendeeFormDataFromProfile(raw),
      completeLater,
      document: completeLater ? undefined : document,
      organizer: match(raw.status)
        .with('PAID_MANUALLY', (paymentStatus) => ({
          paymentStatus,
          paymentMethod: raw.paymentMethod
        }))
        .with(P.union('FREE', 'PAID', 'PENDING'), (paymentStatus) => ({
          paymentStatus
        }))
        .with(P.union('WAITING_LIST', P.nullish), () => ({
          paymentStatus: null
        }))
        .exhaustive(),
      options
    }
    return newData
  }

  shouldDisableMainJustificativeForm(
    attendeeFormData: AttendeeFormData
  ): boolean {
    return match(attendeeFormData)
      .with(
        {
          document: {
            completeType: 'LICENSE_AUTO_VERIFIED',
            documentVerification: { verificationStatus: 'VERIFIED' }
          }
        },
        () => true
      )
      .with(
        {
          document: {
            completeType: 'FFA_PPS',
            documentVerification: { verificationStatus: 'VERIFIED' }
          }
        },
        () => true
      )
      .with(
        {
          document: {
            completeType: 'CHECKBOX_AUTHORIZATION'
          }
        },
        ({ document: { checked } }) => checked === true
      )
      .with(
        {
          document: {
            completeType: 'HEALTH_QUESTIONNAIRE'
          }
        },
        ({ document: { answers } }) =>
          !!answers &&
          Object.entries(answers).every(([_, value]) => value === false)
      )
      .with(
        {
          document: {
            completeType: 'FFTRI_BUY_PASS_COMPETITION',
            healthInfoFormCompleted: true,
            passAdded: true
          }
        },
        () => true
      )
      .otherwise(() => false)
  }

  getAttendeeFormDataFromProfile(
    raw: AttendeeProfileForReset,
    formOptions: Record<
      string,
      { key: string; value: FormSchema['defaultValue']; completeLater: boolean }
    > = {}
  ): AttendeeFormData {
    const data: AttendeeProfileForReset = {
      ...raw,
      // birthdate is a string in the raw data (type issue)
      birthDate: raw.birthDate ? z.coerce.date().parse(raw.birthDate) : null
    }

    const newData: AttendeeFormData = {
      ...defaultAttendeeFormValues,
      options: Record.map(formOptions, (option) => ({
        ...option,
        completeLater: false
      })),
      particulars: {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender ?? undefined,
        dateOfBirth: data.birthDate
          ? z.coerce.date().parse(data.birthDate)
          : undefined,
        phone: data.phoneNumber ?? undefined,
        nationality: data.nationality ?? undefined,
        address: data.postalAddressMain
          ? {
              postalAddressMain: data.postalAddressMain,
              postalAddressSecondary: data.postalAddressSecondary ?? '',
              postalAddressZipcode: data.postalAddressZipcode ?? '',
              postalAddressCity: data.postalAddressCity ?? '',
              postalAddressCountry: data.postalAddressCountry ?? '',
              postalAddressAreaLevel1: data.postalAddressAreaLevel1 ?? '',
              postalAddressAreaLevel2: data.postalAddressAreaLevel2 ?? ''
            }
          : undefined
      },
      misc: {
        clubOrAssociation: data.clubOrAssociation ?? undefined,
        hideInResults: data.hideInResults ?? undefined
      }
    }

    return newData
  }

  getAdultJustificativeOptionsMap() {
    const shouldShowPPS =
      this.shouldShowAlternativeFFAPPS() ||
      this.shouldShowAlternativeFFAPPSTooEarly()
    const shouldShowMedicalCertificate =
      this.shouldShowAlternativeFFAPPSMedicalCertificate()

    return {
      [AdultJustificativeType.MEDICAL_CERTIFICATE]: shouldShowPPS
        ? shouldShowMedicalCertificate
          ? 'races.racePage.justificative.adultJustificativeOptions.PPSOrMedicalCertificate'
          : 'races.racePage.justificative.adultJustificativeOptions.onlyPPS'
        : 'races.racePage.justificative.adultJustificativeOptions.medicalCertificate',
      [AdultJustificativeType.NONE]: 'races.racePage.justificative.none'
    } as const
  }

  isTooEarly(
    completeType: AttendeeFormDataDocument['completeType'] | undefined
  ) {
    if (
      isSpecialFFAPatternWithPPS(this.attendeeFormContext.accessRule) &&
      completeType &&
      completeType === 'FFA_PPS_TOO_EARLY'
    ) {
      return {
        date: sub(this.attendeeFormContext.raceDate, { months: 3 })
      }
    }

    return {
      date: null
    }
  }
}

const fromLicenseAccessRule =
  (data: AttendeeForReset) => (accessRule: Omit<LicenseOnly, 'type'>) => {
    const fillJustification = getMainFillJustification(data.fillJustifications)
    const result: Pick<AttendeeFormData, 'completeLater' | 'document'> = {
      completeLater:
        fillJustification?.status === 'UNSUBMITTED' ||
        !!data.fillJustificationLaterId
    }
    if (!result.completeLater) {
      const document: AttendeeFormDataDocument = {
        completeType:
          accessRule.specialFederationWithId &&
          accessRule.specialFederationWithId.name === data.federationName
            ? 'LICENSE_AUTO_VERIFIED'
            : 'LICENSE_MANUALLY_VERIFIED',
        federationName: data.federationName ?? undefined,
        documentNumber: data.licenseNumber ?? undefined
      }

      if (data.licenseNumber) {
        document.documentVerification = match<
          AttendeeForReset,
          AttendeeFormDataDocumentVerification
        >(data)
          .with({ licenseNumberInvalidAtTimeOfRace: true }, () => ({
            uploadedDocumentKey: data.medicalCertificate?.key,
            verificationStatus: 'REQUIRES_RENEWAL'
          }))
          .otherwise(({ medicalCertificate }) => ({
            uploadedDocumentKey: medicalCertificate?.key,
            verificationStatus:
              document.completeType === 'LICENSE_MANUALLY_VERIFIED'
                ? 'MANUALLY_VERIFIED'
                : data.medicalCertificate?.status === 'VALID'
                ? 'VERIFIED'
                : data.medicalCertificate
                ? data.medicalCertificate.status ?? undefined
                : 'VERIFIED',
            verifiedAt: new Date()
          }))
      }
      result.document = document
    }
    return result
  }

const fromFFTRIPassCompetition = (data: AttendeeForReset) => {
  const fillJustification = getMainFillJustification(data.fillJustifications)
  const result: Pick<AttendeeFormData, 'completeLater' | 'document'> = {
    completeLater:
      fillJustification?.status === 'UNSUBMITTED' ||
      !!data.fillJustificationLaterId
  }
  result.document = { completeType: 'FFTRI_BUY_PASS_COMPETITION' }
  if (data.completedCompetitionPass) {
    result.document.healthInfoFormCompleted = true
    result.document.passAdded = true
  }
  return result
}

const fromSupportingDocumentAccessRule =
  (data: AttendeeForReset) =>
  (
    accessRule: Omit<SupportingDocumentOnly, 'type'>,
    ctx: MakeAttendeeFormSchemaContext
  ): Pick<AttendeeFormData, 'completeLater' | 'document'> => {
    const isAttendeeMinor =
      data.birthDate && isMinor(data.birthDate, ctx.raceDate)
    const fillJustification = getMainFillJustification(data.fillJustifications)
    const result: Pick<AttendeeFormData, 'completeLater' | 'document'> = {
      completeLater:
        fillJustification?.status === 'UNSUBMITTED' ||
        !!data.fillJustificationLaterId
    }
    if (isAttendeeMinor === null) {
      return result
    }
    if (isAttendeeMinor) {
      result.document = match(accessRule)
        .with(
          { acceptedMinorSupportingDocument: 'NONE' },
          () => ({ completeType: 'NO_SUPPORTING_DOCUMENT_REQUIRED' } as const)
        )
        .with(
          {
            acceptedMinorSupportingDocument: 'CHECKBOX_AUTHORIZATION'
          },
          ({ acceptedMinorSupportingDocument }) => ({
            completeType: acceptedMinorSupportingDocument,
            checked: data.hasParentalAuthorization ?? undefined
          })
        )
        .with(
          {
            acceptedMinorSupportingDocument: 'PARENTAL_AUTHORIZATION'
          },
          ({ acceptedMinorSupportingDocument }) => ({
            completeType: acceptedMinorSupportingDocument,
            uploadedDocumentKey: data.medicalCertificate?.key
          })
        )
        .with(
          {
            acceptedMinorSupportingDocument: 'HEALTH_QUESTIONNAIRE'
          },
          ({ acceptedMinorSupportingDocument }) => ({
            completeType: acceptedMinorSupportingDocument,
            answers: data.hasParentalAuthorization
              ? allAnswersToFalse
              : undefined
          })
        )
        .exhaustive()

      return result
    }

    // Below is for adult case
    result.document = {
      completeType: match(accessRule)
        .with(
          { acceptedAdultSupportingDocument: 'NONE' },
          () => 'NO_SUPPORTING_DOCUMENT_REQUIRED' as const
        )
        .with(
          { acceptedAdultSupportingDocument: P.not('NONE') },
          ({ acceptedAdultSupportingDocument }) =>
            acceptedAdultSupportingDocument
        )
        .exhaustive(),
      uploadedDocumentKey: data.medicalCertificate?.key
    }
    return result
  }

export type AttendeeForReset = Omit<
  Attendee,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'race'
  | 'isRaceWithJustificative'
  | 'isRegisteredByOrganizer'
  | 'raceId'
  | 'cartId'
  | 'orderId'
  | 'boughtAt'
  | 'bibNumber'
  | 'bibTeamNumber'
  | 'hasPaidParticipation'
  | 'bibWithdrawalDate'
  | 'formsAndResponses'
  | 'ageCategoryLabelOnRace'
  | 'cart'
  | 'registrationStatus'
  | 'formTitleFromKey'
  | 'language'
  | 'paymentLinkSent'
  | 'isOffStock' // TODO: this field will soon be deleted from Attendees table
>

export type AttendeeProfileForReset = Omit<
  AttendeeForReset,
  | 'status'
  | 'paymentMethod'
  | 'federationName'
  | 'licenseNumberInvalidAtTimeOfRace'
  | 'licenseNumber'
  | 'medicalCertificate'
  | 'hasParentalAuthorization'
  | 'completedCompetitionPass'
  | 'fillJustifications'
  | 'fillJustificationLaterId'
  | 'attendeeProfileId'
  | 'groupId'
  | 'isOffStock' // TODO: this field will soon be deleted from Attendees table
  | 'draftOrderId'
  | 'thirdPartyDocumentStatus'
  | 'cancelledAt'
>

export const defaultAttendeeFormValues: AttendeeFormData = {
  particulars: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: undefined,
    gender: undefined,
    nationality: '',
    address: undefined
  },
  completeLater: false,
  options: {},
  misc: {
    clubOrAssociation: '',
    hideInResults: false
  }
}
